import { Grid, Chip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useState, useEffect } from 'react';
import MainCard from 'ui-component/cards/MainCard';
import { get, put } from '../../axios/apiService';
import LoadingBackdrop from 'ui-component/full-screen-loadder/LoadingBackdrop';
import CustomNoRowsOverlay from 'ui-component/customNoRowsOverlay';
import SearchIcon from '@mui/icons-material/Search';
import { Modules } from 'store/constant';
import SearchBar from 'ui-component/SearchBar/SearchBar';
import AccountStatusModal from 'ui-component/Modals/AccountStatusModal';
import AccountActionButtons from 'ui-component/actionButton/AccountActionButtons';
import toast from 'react-hot-toast';
import dayjs from 'dayjs';
import { usePermissions } from 'hooks/usePermissions';
import { MODULE_KEYS } from 'store/constant';

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px solid #11111111',
  boxShadow: 24,
  p: 4
};

const Accounts = () => {
  const [isLoading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [permissionMatrix, setPermissionMatrix] = useState({});
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [deleteId, setDeleteId] = useState('');
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const { hasWritePermission } = usePermissions();
  const canEdit = hasWritePermission(MODULE_KEYS.Accounts);


  const handleStatusUpdate = async ({ verificationStatus, remark }) => {
    if (!selectedAccount) return;

    if (verificationStatus === undefined || verificationStatus === null) {
      toast.error("Please select a verification status.");
      return;
    }
    if (!remark || !remark.trim()) {
      toast.error("Remark is required.");
      return;
    }
    setLoading(true);
    const payload = {
      id: selectedAccount.id,
      accountUid: selectedAccount.accountUid,
      verificationStatus,
      remark
    };

    try {
      const response = await put("Accounts", payload);
      toast.success("Account status updated successfully!");
      setPaginationModel((prev) => ({ ...prev }));
    } catch (error) {
      console.error(error);
      toast.error("Failed to send email or update status");
    } finally {
      setLoading(false);
      setStatusModalOpen(false);
    }
  };

  // ============= FETCH ACCOUNTS =============
  useEffect(() => {
    const fetchAccounts = async () => {
      setLoading(true);

      try {
        const defaultSortField = "StartDate";
        const defaultSortOrder = "Descending";

        const sortField = sortModel.length
          ? sortAccountField(sortModel[0].field)
          : defaultSortField;

        const sortOrder = sortModel.length
          ? (sortModel[0].sort === 'asc' ? 'Ascending' : 'Descending')
          : defaultSortOrder;

        const sortQuery = `&$orderby=${sortField}&$orderbydirection=${sortOrder}`;

        const encoded = encodeURIComponent(search);
        const filterQuery = search
          ? `&$filter=Email~LIKE~${encoded} OR FirstName~LIKE~${encoded} OR Email~LIKE~${encoded} OR AccountStageLabel~LIKE~${encoded}`
          : '';

        const skip = paginationModel.page * paginationModel.pageSize;
        const top = paginationModel.pageSize;

        const url = `Accounts?$skip=${skip}&$top=${top}${filterQuery}${sortQuery}`;

        const response = await get(url, null);

        if (response && response.values) {
          setAccounts(response.values);
          setRowCount(response.totalCount);
        } else {
          setAccounts([]);
          setRowCount(0);
        }
      } catch (err) {
        console.error('Failed to load accounts', err);
      } finally {
        setLoading(false);
      }
    };

    fetchAccounts();
  }, [paginationModel, sortModel, search]);



  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortModelChange = (model) => {
    setSortModel(model);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleClose = () => {
    setOpen(false);
    setDeleteId('');
  };

  // ============= VERIFICATION STATUS FORMATTER =============
  const formatStatus = (value) => {
    const map = {
      0: { label: "Pending", color: "warning" },
      1: { label: "Verified", color: "success" },
      2: { label: "Rejected", color: "error" }
    };

    const status = map[value] || { label: "Unknown", color: "default" };

    return (
      <Chip
        label={status.label}
        color={status.color}
        size="small"
      />
    );
  };

  // ============= COLUMNS =============
  const columns = [
    { field: 'email', headerName: 'Email', flex: 1 },
    { field: 'firstName', headerName: 'First Name', flex: 1 },
    { field: 'lastName', headerName: 'Last Name', flex: 1 },
    { field: 'accountUid', headerName: 'Account UID', flex: 1 },
    { field: 'accountStageLabel', headerName: 'Stage', flex: 1 },
    { 
      field: 'startDate', 
      headerName: 'Start Date', 
      flex: 1,
      renderCell: (params) => dayjs(params.value).format('DD-MMM-YYYY')
    },
    { 
      field: 'renewalDate', 
      headerName: 'Renewal Date', 
      flex: 1,
      renderCell: (params) => dayjs(params.value).format('DD-MMM-YYYY')
    },
    {
      field: 'verificationStatus',
      headerName: 'Verification Status',
      flex: 1,
      renderCell: (params) => formatStatus(params.value)
    },
    {
      field: 'action',
      headerName: 'Actions',
      renderCell: (params) => (
        <AccountActionButtons
          row={params.row}
          onEdit={(row) => {
            setSelectedAccount(row);
            setStatusModalOpen(true);
          }}
          canEdit={canEdit}
          onView={(row) => {
            setSelectedAccount(row);
            setStatusModalOpen(true);
          }}
        />
      ),
      width: 100,
      sortable: false,
      filterable: false
    }

  ];

  return (
    <>
      <MainCard title="Accounts">
        <Grid container spacing={3}>
          {/* SEARCH + ADD BUTTON */}
          <Grid item xs={12}>
            <SearchBar
              value={search}
              onChange={handleSearchChange}
            />
          </Grid>

          {/* DATA GRID */}
          <Grid item xs={12}>
            <DataGrid
              getRowId={(row) => row.id}
              rows={accounts}
              rowCount={rowCount}
              columns={columns}
              paginationMode="server"
              sortingMode="server"
              pageSizeOptions={[10]}
              paginationModel={paginationModel}
              onPaginationModelChange={setPaginationModel}
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              components={{ NoRowsOverlay: CustomNoRowsOverlay }}
              columnVisibilityModel={{
                action: permissionMatrix?.writeRights
              }}
            />
          </Grid>
        </Grid>
      </MainCard>
      <LoadingBackdrop open={isLoading} />
      <AccountStatusModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        account={selectedAccount}
        onSave={(newData) => handleStatusUpdate(newData)}
        canEdit={canEdit}
      />

    </>
  );
};

export default Accounts;

// MAPPING SORT FIELDS
const sortAccountField = (field) => {
  if (field === 'email') return 'Email';
  if (field === 'firstName') return 'FirstName';
  if (field === 'lastName') return 'LastName';
  if (field === 'accountUid') return 'AccountUid';
  if (field === 'accountStageLabel') return 'AccountStageLabel';
  if (field === 'startDate') return 'StartDate';
  if (field === 'renewalDate') return 'RenewalDate';
  if (field === 'verificationStatus') return 'VerificationStatus';
  return '';
};

